import { Spacing } from '@persona-id/design-tokens';
import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import NavBack from 'dashboard/components/NavBack';
import UnauthenticatedPage from 'dashboard/components/UnauthenticatedPage';
import FormMfaCode from 'dashboard/forms/FormMfaCode';
import { FormMfaCodeData } from 'dashboard/forms/FormMfaCode/FormMfaCode';
import { MfaMethod } from 'dashboard/models/user';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { Heading, Paragraph } from 'design-system/components/Text';
import IconArrowRight from 'images/icons/arrow-right.svg';
import IconEmail from 'images/icons/email.svg';
import IconPhone from 'images/icons/phone.svg';
import IconShield from 'images/icons/shield.svg';

const StyledNavBack = styled(NavBack)`
  margin-bottom: ${Spacing[2]};
`;

const MfaSelectionContainer = styled.div`
  display: grid;
  grid-gap: ${Spacing[2]};
  width: 100%;
`;

interface DashboardMfaCodeViewProps {
  loading: boolean;
  startMfa: (method: MfaMethod) => Promise<void>;
  resendCode: (method: MfaMethod.Email | MfaMethod.Phone) => Promise<void>;
  activateMfa: (method: MfaMethod, code: string) => Promise<void>;
  availableMfaMethods: MfaMethod[];
  startedMfaMethod: MfaMethod | undefined;
  cancelMfa: () => void;
  clearAuthenticationState: () => void;
}

const DashboardMfaCodeView: FC<DashboardMfaCodeViewProps> = ({
  loading,
  startMfa,
  resendCode,
  activateMfa,
  availableMfaMethods,
  startedMfaMethod,
  cancelMfa,
  clearAuthenticationState,
}) => {
  const navigate = useNavigate();

  const handleSubmit = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    ({ code }: FormMfaCodeData) => activateMfa(startedMfaMethod, code),
    [activateMfa, startedMfaMethod],
  );

  const handleCancel = useCallback(() => {
    if (availableMfaMethods.length === 1) {
      navigate('/dashboard/login');
      clearAuthenticationState();
    } else {
      cancelMfa();
    }
  }, [availableMfaMethods]);

  // If there are >1 methods, it will show in the selection screen. Selection
  // screen is skipped if there is only one method.
  const showRecoveryCodeSectionInForm =
    availableMfaMethods.length === 1 && startedMfaMethod !== MfaMethod.RecoveryCode;

  const recoveryCodeSection = (
    <>
      <Divider />
      <Button
        id='dashboard-login-mfa__btn--recovery-code'
        data-test='dashboard-login-mfa__btn--recovery-code'
        width='auto'
        variant='inverse'
        onClick={() => startMfa(MfaMethod.RecoveryCode)}
        disabled={loading}
      >
        I no longer have access to my 2FA device
      </Button>
    </>
  );

  if (startedMfaMethod == null) {
    return availableMfaMethods.length === 0 ? (
      <UnauthenticatedPage>
        <Heading size='sm'>2-factor authentication</Heading>
        <Spacer height={2.5} />
        <MfaSelectionContainer>
          <StyledNavBack
            id='dashboard-login-mfa__nav-back'
            to='/dashboard/login'
            onClick={clearAuthenticationState}
          >
            Back
          </StyledNavBack>
          <Paragraph data-test='dashboard-login-mfa__locked-out-text'>
            Your organization requires 2-factor authentication, but you do not have any 2FA methods
            set up. Please request a recovery code from your administrator to regain access to your
            account.
          </Paragraph>
          <Spacer />
          <Button
            id='dashboard-login-mfa__btn--recovery-code'
            data-test='dashboard-login-mfa__btn--recovery-code'
            onClick={() => startMfa(MfaMethod.RecoveryCode)}
            disabled={loading}
          >
            Enter recovery code
          </Button>
        </MfaSelectionContainer>
      </UnauthenticatedPage>
    ) : (
      <UnauthenticatedPage>
        <Heading size='sm'>2-factor authentication</Heading>
        <Spacer height={2.5} />
        <MfaSelectionContainer>
          <StyledNavBack
            id='dashboard-login-mfa__nav-back'
            to='/dashboard/login'
            onClick={clearAuthenticationState}
          >
            Back
          </StyledNavBack>
          <Paragraph>This extra step shows that it&apos;s really you trying to sign in.</Paragraph>
          {availableMfaMethods.includes(MfaMethod.Totp) && (
            <Button
              id='dashboard-login-mfa__btn--totp'
              data-test='dashboard-login-mfa__btn--totp'
              variant='basic'
              prefixIcon={<IconShield />}
              suffixIcon={<IconArrowRight />}
              onClick={() => startMfa(MfaMethod.Totp)}
              disabled={loading}
            >
              Use your authenticator app
            </Button>
          )}
          {availableMfaMethods.includes(MfaMethod.Email) && (
            <Button
              id='dashboard-login-mfa__btn--email'
              data-test='dashboard-login-mfa__btn--email'
              variant='basic'
              prefixIcon={<IconEmail />}
              suffixIcon={<IconArrowRight />}
              onClick={() => startMfa(MfaMethod.Email)}
              disabled={loading}
            >
              Get a code sent to your email
            </Button>
          )}
          {availableMfaMethods.includes(MfaMethod.Phone) && (
            <Button
              id='dashboard-login-mfa__btn--phone'
              data-test='dashboard-login-mfa__btn--phone'
              variant='basic'
              prefixIcon={<IconPhone />}
              suffixIcon={<IconArrowRight />}
              onClick={() => startMfa(MfaMethod.Phone)}
              disabled={loading}
            >
              Get a code sent to your phone
            </Button>
          )}
          {recoveryCodeSection}
        </MfaSelectionContainer>
      </UnauthenticatedPage>
    );
  }

  return (
    <UnauthenticatedPage>
      <Heading size='sm'>2-factor authentication</Heading>

      <Spacer height={2.5} />

      <Paragraph>{MFA_PROMPT[startedMfaMethod]}</Paragraph>

      <Spacer height={2.5} />

      <FormMfaCode
        type={startedMfaMethod === MfaMethod.RecoveryCode ? 'text' : 'pin'}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onResend={
          isMfaMethodResendable(startedMfaMethod) ? () => resendCode(startedMfaMethod) : undefined
        }
        resendLoading={loading}
      />
      {showRecoveryCodeSectionInForm && recoveryCodeSection}
    </UnauthenticatedPage>
  );
};

export default DashboardMfaCodeView;

const MFA_PROMPT: Record<MfaMethod, string> = {
  [MfaMethod.Totp]: 'Enter the verification code generated from your authenticator app.',
  [MfaMethod.Email]: 'Enter the confirmation code we sent to your email.',
  [MfaMethod.Phone]: 'Enter the confirmation code we sent to your phone.',
  [MfaMethod.RecoveryCode]: 'Enter your one-time recovery code.',
};

const isMfaMethodResendable = (
  mfaMethod: MfaMethod,
): mfaMethod is MfaMethod.Phone | MfaMethod.Email =>
  mfaMethod === MfaMethod.Phone || mfaMethod === MfaMethod.Email;
